import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { crrFlashQuestions, type CRRFlashQuestion } from '@/data/crr-flash-questions';
import { cotierFlashQuestions } from '@/data/cotier-flash-questions';
import { fluvialFlashQuestions } from '@/data/fluvial-flash-questions';
import { jetskiFlashQuestions } from '@/data/jetski-flash-questions';
import type { FlashDeckType } from '@/lib/flashMemoryStore';

export interface FlashCard {
  id: string;
  question_fr: string;
  question_en: string;
  answer_fr: string;
  answer_en: string;
  context_fr: string;
  context_en: string;
  simple_fr?: string;
  category: string;
  acronym?: string;
  explanation_fr?: string;
  explanation_en?: string;
  use_cases_fr?: string[];
  use_cases_en?: string[];
  tags?: string[];
}

function getLocalCards(deck: FlashDeckType): FlashCard[] {
  const mapToFlash = (q: any): FlashCard => ({
    ...q,
    explanation_fr: q.context_fr,
    explanation_en: q.context_en,
    use_cases_fr: [],
    use_cases_en: [],
    tags: [],
  });

  switch (deck) {
    case 'cotier':
      return cotierFlashQuestions.map(mapToFlash);
    case 'fluvial':
      return fluvialFlashQuestions.map(mapToFlash);
    case 'jetski':
      return jetskiFlashQuestions.map(mapToFlash);
    case 'crr':
    default:
      return crrFlashQuestions.map(mapToFlash);
  }
}

async function fetchFlashcards(deck: FlashDeckType): Promise<FlashCard[]> {
  // Only CRR has Supabase backend data currently
  if (deck !== 'crr') {
    return getLocalCards(deck);
  }

  const { data, error } = await supabase
    .from('flashcards')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: true });

  if (error || !data || data.length === 0) {
    return getLocalCards('crr');
  }

  return data.map((row) => ({
    id: row.id,
    acronym: row.acronym ?? undefined,
    question_fr: row.question_fr,
    question_en: row.question_en,
    answer_fr: row.answer_fr,
    answer_en: row.answer_en,
    context_fr: row.explanation_fr ?? '',
    context_en: row.explanation_en ?? '',
    explanation_fr: row.explanation_fr ?? undefined,
    explanation_en: row.explanation_en ?? undefined,
    use_cases_fr: row.use_cases_fr ?? [],
    use_cases_en: row.use_cases_en ?? [],
    simple_fr: undefined,
    category: (row.category as string) || 'general',
    tags: row.tags ?? [],
  }));
}

export function useFlashDeck(deck: FlashDeckType = 'crr') {
  return useQuery<FlashCard[]>({
    queryKey: ['flashcards', deck],
    queryFn: () => fetchFlashcards(deck),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: typeof window !== 'undefined',
    initialData: undefined,
    placeholderData: [],
  });
}
